import { useState, useCallback, useRef, useEffect } from 'react'

export interface SSEOptions {
  onMessage?: (data: any) => void
  onError?: (error: Error) => void
  onComplete?: () => void
  headers?: Record<string, string>
}

export interface SSEState {
  isConnected: boolean
  isStreaming: boolean
  error: Error | null
  lastEvent: any | null
}

/**
 * Hook for handling Server-Sent Events
 */
export function useSSE(options: SSEOptions = {}) {
  const [state, setState] = useState<SSEState>({
    isConnected: false,
    isStreaming: false,
    error: null,
    lastEvent: null,
  })
  
  const abortControllerRef = useRef<AbortController | null>(null)
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null)
  
  // Start SSE connection
  const connect = useCallback(async (url: string, body?: any) => {
    // Abort any existing connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    
    abortControllerRef.current = new AbortController()
    
    setState((prev) => ({
      ...prev,
      isConnected: true,
      isStreaming: true,
      error: null,
    }))
    
    try {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...options.headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: abortControllerRef.current.signal,
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body')
      }
      
      readerRef.current = reader
      const decoder = new TextDecoder()
      let buffer = ''
      
      while (true) {
        const { done, value } = await reader.read()
        
        if (done) {
          break
        }
        
        buffer += decoder.decode(value, { stream: true })
        
        // Process complete events
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6)
            
            try {
              const data = JSON.parse(dataStr)
              
              setState((prev) => ({
                ...prev,
                lastEvent: data,
              }))
              
              options.onMessage?.(data)
              
              // Check for completion events
              if (data.type?.includes('complete') || data.type === 'error') {
                break
              }
            } catch (e) {
              console.warn('Failed to parse SSE data:', dataStr)
            }
          }
        }
      }
      
      setState((prev) => ({
        ...prev,
        isStreaming: false,
      }))
      
      options.onComplete?.()
      
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // Connection was aborted intentionally
        return
      }
      
      const err = error instanceof Error ? error : new Error(String(error))
      
      setState((prev) => ({
        ...prev,
        isConnected: false,
        isStreaming: false,
        error: err,
      }))
      
      options.onError?.(err)
    }
  }, [options])
  
  // Disconnect
  const disconnect = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    
    if (readerRef.current) {
      readerRef.current.cancel()
      readerRef.current = null
    }
    
    setState({
      isConnected: false,
      isStreaming: false,
      error: null,
      lastEvent: null,
    })
  }, [])
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])
  
  return {
    ...state,
    connect,
    disconnect,
  }
}

/**
 * Hook for streaming content accumulation
 */
export function useStreamingContent() {
  const [content, setContent] = useState('')
  const [isComplete, setIsComplete] = useState(false)
  
  const append = useCallback((chunk: string) => {
    setContent((prev) => prev + chunk)
  }, [])
  
  const reset = useCallback(() => {
    setContent('')
    setIsComplete(false)
  }, [])
  
  const complete = useCallback(() => {
    setIsComplete(true)
  }, [])
  
  return {
    content,
    isComplete,
    append,
    reset,
    complete,
  }
}

export default useSSE
